import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import * as LucideIcons from 'lucide-react';
import { features } from '../mock';

export const Features = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: false, amount: 0.1 });

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.6,
        ease: [0.4, 0, 0.3, 1],
      },
    }),
  };

  return (
    <section
      id="features"
      ref={containerRef}
      className="py-24 relative overflow-hidden"
      style={{ backgroundColor: 'white' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: isInView ? 1 : 0, y: isInView ? 0 : 30 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2
            className="text-4xl md:text-5xl font-bold mb-6"
            style={{ color: '#283750' }}
          >
            Everything You Need to
            <br />
            <span style={{ color: '#000' }}>Manage Events</span>
          </h2>
          <p
            className="text-xl max-w-3xl mx-auto"
            style={{ color: '#736c64' }}
          >
            From creation to check-in, our comprehensive platform handles every
            aspect of event management with ease and efficiency.
          </p>
        </motion.div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => {
            const IconComponent = LucideIcons[feature.icon];
            
            return (
              <motion.div
                key={feature.id}
                custom={index}
                variants={cardVariants}
                initial="hidden"
                animate={isInView ? 'visible' : 'hidden'}
                whileHover={{
                  y: -10,
                  transition: { duration: 0.3 },
                }}
                className="group relative p-8 rounded-2xl transition-all duration-500 cursor-pointer"
                style={{
                  backgroundColor: 'transparent',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                }}
              >
                {/* Icon */}
                <motion.div
                  whileHover={{ rotate: 360, scale: 1.1 }}
                  transition={{ duration: 0.6 }}
                  className="w-16 h-16 rounded-xl flex items-center justify-center mb-6"
                  style={{
                    backgroundColor: feature.color,
                    opacity: 0.9,
                  }}
                >
                  {IconComponent && (
                    <IconComponent size={32} style={{ color: 'white' }} />
                  )}
                </motion.div>

                {/* Content */}
                <h3
                  className="text-2xl font-bold mb-4 group-hover:opacity-80 transition-opacity"
                  style={{ color: '#283750' }}
                >
                  {feature.title}
                </h3>
                <p
                  className="text-base leading-relaxed"
                  style={{ color: '#736c64' }}
                >
                  {feature.description}
                </p>

                {/* Hover Effect - Border */}
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  whileHover={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.3 }}
                  className="absolute inset-0 rounded-2xl border-2 pointer-events-none"
                  style={{ borderColor: feature.color }}
                />
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
